import { computed, type Ref } from "vue";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { cardTypeName } from "@utils/constants/cardTypes";

export interface CompletionAnswer {
  code: string;
  title: unknown;
  n_screenout?: number;
  screenout_rate?: number;
}

export interface CompletionCard {
  token: string;
  name: string;
  type: string;
  title: unknown;
  rank: number;
  n: number;
  n_remaining: number;
  n_share: number;
  n_screenout: number;
  screenout_rate: number;
  n_drop: number;
  drop_rate: number;
  n_completion: number;
  answers: CompletionAnswer[];
}

export interface CompletionSummary {
  n_started: number;
  n_complete: number;
  n_screenout: number;
  n_drop: number;
  net_completion: number;
  net_screenout: number;
  overall_completion: number;
}

export interface CardMetrics {
  token: string;
  name: string;
  type: string;
  cardType: string;
  title: string;
  n: number;
  nOk: number;
  nDrop: number;
  nScreenout: number;
  reach: number;
  remaining: number;
  notShown: number;
  nNotShown: number;
  ok: number;
  drop: number;
  screenout: number;
  loss: number;
  retention: number;
  dropOfCard: number;
  screenoutOfCard: number;
  netScreenoutOfCard: number;
  answers: { title: string; nScreenout: number; screenoutOfCard: number }[];
}

export const plainText = (text: string) =>
  (text || "")
    .replace(/<[^>]*>/g, "")
    .replace(/\*\*|__|\\*|_|~~|`/g, "")
    .replace(/\n/g, " ")
    .trim();

export const percent = (value: number) => `${Math.round(value)}%`;

export const useCompletionCards = (cards: Ref<CompletionCard[]>, starts: Ref<number>) => {
  const { localizeText } = useSurveyLocalesStore();
  const ofStarts = (n: number) => (starts.value > 0 ? (n / starts.value) * 100 : 0);
  const ofCard = (n: number, card: CompletionCard) => (card.n > 0 ? Math.round((n / card.n) * 100) : 0);

  return computed<CardMetrics[]>(() =>
    cards.value.map((card) => ({
      token: card.token,
      name: card.name,
      type: cardTypeName(card.type),
      cardType: card.type,
      title: plainText(localizeText(card.title)),
      n: card.n,
      nOk: card.n_completion,
      nDrop: card.n_drop,
      nScreenout: card.n_screenout,
      reach: ofStarts(card.n),
      remaining: ofStarts(card.n_remaining),
      notShown: ofStarts(Math.max(0, card.n_remaining - card.n)),
      nNotShown: Math.max(0, card.n_remaining - card.n),
      ok: ofStarts(card.n_completion),
      drop: ofStarts(card.n_drop),
      screenout: ofStarts(card.n_screenout),
      loss: ofStarts(card.n_drop + card.n_screenout),
      retention: (1 - card.n_share * card.drop_rate) * (1 - card.n_share * card.screenout_rate),
      dropOfCard: ofCard(card.n_drop, card),
      screenoutOfCard: ofCard(card.n_screenout, card),
      netScreenoutOfCard: Math.round((card.screenout_rate || 0) * 100),
      answers: (card.answers || []).map((answer) => ({
        title: plainText(localizeText(answer.title)),
        nScreenout: answer.n_screenout ?? 0,
        screenoutOfCard: Math.round((answer.screenout_rate || 0) * 100),
      })),
    })),
  );
};
